import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Layout } from "../components/Layout";
import { QUESTIONS } from "../lib/quiz";
import { api } from "../lib/api";
import { ArrowLeft, ArrowRight, Loader2, Check } from "lucide-react";

export default function Quiz() {
    const navigate = useNavigate();
    const [lead, setLead] = useState(null);
    const [answers, setAnswers] = useState({});
    const [step, setStep] = useState(0);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        const raw = sessionStorage.getItem("ash_lead");
        if (!raw) {
            navigate("/lead");
            return;
        }
        setLead(JSON.parse(raw));
        const savedA = sessionStorage.getItem("ash_answers");
        if (savedA) setAnswers(JSON.parse(savedA));
    }, [navigate]);

    const q = QUESTIONS[step];
    const total = QUESTIONS.length;

    const isAnswered = (qq, ans) => {
        const v = ans[qq.id];
        if (qq.multi) return Array.isArray(v) && v.length > 0;
        return v !== undefined && v !== null && v !== "";
    };

    const progress = useMemo(() => {
        if (!q) return 0;
        return Math.round(((step + (isAnswered(q, answers) ? 1 : 0)) / total) * 100);
    }, [step, answers, q, total]);

    const isSelected = (opt) => {
        if (!q) return false;
        if (q.multi) return Array.isArray(answers[q.id]) && answers[q.id].includes(opt);
        if (q.scale) return answers[q.id] === opt;
        return answers[q.id] === opt;
    };

    const select = (val) => {
        let next;
        if (q.multi) {
            const current = Array.isArray(answers[q.id]) ? answers[q.id] : [];
            const exclusiveSet = new Set(q.exclusive || []);
            const isClicking = !current.includes(val);
            let updated;
            if (exclusiveSet.has(val)) {
                // Clicking an exclusive option: select it alone, or clear if it was the only thing
                updated = isClicking ? [val] : [];
            } else if (isClicking) {
                // Clicking a normal option: add it and drop any exclusive options
                updated = current.filter((x) => !exclusiveSet.has(x)).concat([val]);
            } else {
                // Deselecting a normal option
                updated = current.filter((x) => x !== val);
            }
            next = { ...answers, [q.id]: updated };
        } else {
            next = { ...answers, [q.id]: val };
        }
        setAnswers(next);
        sessionStorage.setItem("ash_answers", JSON.stringify(next));
    };

    const goNext = async () => {
        if (!isAnswered(q, answers)) return;
        if (step < total - 1) {
            setStep(step + 1);
            window.scrollTo({ top: 0, behavior: "smooth" });
        } else {
            await submit();
        }
    };

    const goBack = () => {
        if (step > 0) {
            setStep(step - 1);
            window.scrollTo({ top: 0, behavior: "smooth" });
        }
    };

    const submit = async () => {
        setSubmitting(true);
        setError("");
        try {
            const payload = { ...lead, answers };
            const { data } = await api.post("/submissions", payload);
            sessionStorage.removeItem("ash_lead");
            sessionStorage.removeItem("ash_answers");
            navigate(`/results/${data.id}`);
        } catch (e) {
            console.error(e);
            setError(e.response?.data?.detail || "Something went wrong. Please try again.");
            setSubmitting(false);
        }
    };

    if (!q) return null;

    const multiCount = q.multi && Array.isArray(answers[q.id]) ? answers[q.id].length : 0;

    return (
        <Layout minimal>
            <section className="max-w-2xl mx-auto px-6 md:px-10 py-10 md:py-14" data-testid="quiz-section">
                {/* Progress */}
                <div className="mb-10">
                    <div className="flex justify-between items-center text-xs uppercase tracking-widest font-bold text-ash-gray-dark mb-3">
                        <span data-testid="quiz-progress-label">
                            Question {step + 1} of {total}
                        </span>
                        <span>{progress}%</span>
                    </div>
                    <div className="h-1 w-full bg-ash-gray-light overflow-hidden">
                        <div
                            className="h-full bg-ash-orange transition-all duration-300"
                            style={{ width: `${progress}%` }}
                            data-testid="quiz-progress-bar"
                        />
                    </div>
                </div>

                <div className="fade-in" key={q.id}>
                    <h1
                        className="font-display font-bold text-3xl sm:text-4xl tracking-tight text-ash-black leading-tight"
                        data-testid="quiz-question-title"
                    >
                        {q.title}
                    </h1>

                    {q.multi && (
                        <div className="mt-3 text-sm text-ash-gray-dark" data-testid="multi-hint">
                            Pick all that apply{multiCount > 0 ? ` — ${multiCount} selected` : ""}
                        </div>
                    )}

                    <div className="mt-8 space-y-3">
                        {q.scale ? (
                            <div className="grid grid-cols-1 sm:grid-cols-5 gap-3">
                                {q.options.map((o) => (
                                    <button
                                        key={o.value}
                                        type="button"
                                        className="quiz-option text-center"
                                        data-selected={isSelected(o.value)}
                                        data-testid={`quiz-option-${q.id}-${o.value}`}
                                        onClick={() => select(o.value)}
                                    >
                                        <div className="font-display font-black text-2xl text-ash-orange">{o.value}</div>
                                        <div className="text-xs mt-1">{o.label}</div>
                                    </button>
                                ))}
                            </div>
                        ) : (
                            q.options.map((o) => (
                                <button
                                    key={o}
                                    type="button"
                                    className="quiz-option flex items-center justify-between gap-3"
                                    data-selected={isSelected(o)}
                                    data-testid={`quiz-option-${q.id}-${o.replace(/\s+/g, "-").slice(0, 40)}`}
                                    onClick={() => select(o)}
                                >
                                    <span>{o}</span>
                                    {q.multi && (
                                        <span
                                            className={`shrink-0 inline-flex items-center justify-center w-5 h-5 border ${
                                                isSelected(o)
                                                    ? "bg-ash-orange border-ash-orange text-white"
                                                    : "border-ash-gray-light bg-white"
                                            }`}
                                            aria-hidden
                                        >
                                            {isSelected(o) && <Check size={14} strokeWidth={3} />}
                                        </span>
                                    )}
                                </button>
                            ))
                        )}
                    </div>

                    {error && (
                        <div
                            className="mt-6 text-sm text-red-600 bg-red-50 border border-red-200 p-4"
                            data-testid="quiz-error"
                        >
                            {error}
                        </div>
                    )}

                    <div className="mt-10 flex items-center justify-between">
                        <button
                            type="button"
                            className="btn-outline flex items-center gap-2 disabled:opacity-40"
                            onClick={goBack}
                            disabled={step === 0 || submitting}
                            data-testid="quiz-back-btn"
                        >
                            <ArrowLeft size={16} /> Back
                        </button>
                        <button
                            type="button"
                            className="btn-primary"
                            onClick={goNext}
                            disabled={!isAnswered(q, answers) || submitting}
                            data-testid="quiz-next-btn"
                        >
                            {submitting ? (
                                <>
                                    <Loader2 size={18} className="animate-spin" />
                                    Generating your results…
                                </>
                            ) : step === total - 1 ? (
                                <>
                                    View My Results <ArrowRight size={18} />
                                </>
                            ) : (
                                <>
                                    Next <ArrowRight size={18} />
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </section>
        </Layout>
    );
}
